import { useEffect, useRef } from 'react';

const dialogStack = [];
let previousOverflow = '';

// One shared lock lets a nested dialog close without unlocking the page beneath it.
export default function useDialog(active, onClose) {
    const dialogRef = useRef(null);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    useEffect(() => {
        if (!active) return undefined;
        const previousFocus = document.activeElement;
        const dialogToken = Symbol('dialog');
        if (dialogStack.length === 0) {
            previousOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }
        dialogStack.push(dialogToken);
        const isTopmost = () => dialogStack[dialogStack.length - 1] === dialogToken;
        const focusables = () => Array.from(dialogRef.current?.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
        ) || []).filter((element) => !element.hidden && element.getClientRects().length > 0);
        const frame = requestAnimationFrame(() => {
            if (isTopmost()) (focusables()[0] || dialogRef.current)?.focus();
        });
        const onKey = (event) => {
            if (!isTopmost()) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                closeRef.current();
            }
            if (event.key !== 'Tab') return;
            const targets = focusables();
            if (!targets.length) {
                event.preventDefault();
                dialogRef.current?.focus();
                return;
            }
            const first = targets[0];
            const last = targets[targets.length - 1];
            const inside = dialogRef.current?.contains(document.activeElement);
            if (event.shiftKey && (document.activeElement === first || !inside)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            cancelAnimationFrame(frame);
            document.removeEventListener('keydown', onKey);
            const wasTopmost = isTopmost();
            const index = dialogStack.indexOf(dialogToken);
            if (index !== -1) dialogStack.splice(index, 1);
            if (!dialogStack.length) document.body.style.overflow = previousOverflow;
            if (wasTopmost && previousFocus?.isConnected) previousFocus.focus();
        };
    }, [active]);

    return dialogRef;
}
